import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavArrowLeft, Plus, Send } from 'iconoir-react';
import {
  supportApi,
  subscriptionApi,
  getApiErrorMessage,
  SupportTicket,
  SupportTicketCategory,
} from '../services/api';
import '../styles/account-management.css';
import '../styles/support.css';

const CATEGORY_LABELS: Record<SupportTicketCategory, string> = {
  technical: 'Technique',
  billing: 'Facturation',
  feature_request: 'Suggestion',
  bug: 'Bug',
  other: 'Autre',
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Ouvert',
  in_progress: 'En cours',
  waiting_user: 'En attente de votre réponse',
  resolved: 'Résolu',
  closed: 'Fermé',
};

const PRIORITY_LABELS: Record<string, string> = {
  low: 'Basse',
  medium: 'Normale',
  high: 'Prioritaire',
  urgent: 'Urgente',
};

type ViewMode = 'list' | 'create' | 'detail';

export const SupportPage: React.FC = () => {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>('list');
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [hasAds, setHasAds] = useState<boolean | null>(null);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);

  useEffect(() => {
    loadTickets();
    loadSupportLevel();
  }, []);

  const loadTickets = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await supportApi.getAll();
      setTickets(response.data.data || []);
    } catch (err: any) {
      setLoadError(getApiErrorMessage(err, 'Impossible de charger vos tickets de support.'));
    } finally {
      setLoading(false);
    }
  };

  const loadSupportLevel = async () => {
    try {
      const response = await subscriptionApi.getCurrent();
      setHasAds(response.data?.subscription?.plan?.has_ads ?? true);
    } catch {
      setHasAds(null);
    }
  };

  const openTicket = (id: number) => {
    setSelectedTicketId(id);
    setView('detail');
  };

  const backToList = () => {
    setSelectedTicketId(null);
    setView('list');
    loadTickets();
  };

  return (
    <div className="account-management-page">
      <div className="account-header">
        <h1>Support</h1>
        <button className="btn btn-secondary" onClick={() => navigate('/account')}>
          <NavArrowLeft width={18} height={18} strokeWidth={2} />
          Retour au compte
        </button>
      </div>

      {hasAds !== null && (
        <p className="support-level-reminder">
          Votre niveau de support actuel : <strong>{hasAds ? 'Support communautaire' : 'Support prioritaire'}</strong>
        </p>
      )}

      <div className="account-content support-page-content">
        {view === 'list' && (
          <SupportTicketListView
            tickets={tickets}
            loading={loading}
            loadError={loadError}
            onCreate={() => setView('create')}
            onOpen={openTicket}
          />
        )}

        {view === 'create' && (
          <NewTicketForm
            onCancel={() => setView('list')}
            onCreated={(ticket) => {
              setTickets((prev) => [ticket, ...prev]);
              openTicket(ticket.id);
            }}
          />
        )}

        {view === 'detail' && selectedTicketId !== null && (
          <SupportTicketDetailView ticketId={selectedTicketId} onBack={backToList} />
        )}
      </div>
    </div>
  );
};

interface SupportTicketListViewProps {
  tickets: SupportTicket[];
  loading: boolean;
  loadError: string;
  onCreate: () => void;
  onOpen: (id: number) => void;
}

const SupportTicketListView: React.FC<SupportTicketListViewProps> = ({
  tickets,
  loading,
  loadError,
  onCreate,
  onOpen,
}) => (
  <div className="support-tab">
    <div className="support-list-header">
      <h2>Mes tickets</h2>
      <button className="btn btn-primary" onClick={onCreate}>
        <Plus width={18} height={18} strokeWidth={2} />
        Nouveau ticket
      </button>
    </div>

    {loadError && <div className="settings-error">{loadError}</div>}

    {loading ? (
      <div className="loading">Chargement...</div>
    ) : tickets.length === 0 ? (
      <div className="no-documents">Vous n'avez encore ouvert aucun ticket de support.</div>
    ) : (
      <div className="support-ticket-list">
        {tickets.map((ticket) => (
          <button key={ticket.id} className="support-ticket-row" onClick={() => onOpen(ticket.id)}>
            <div className="support-ticket-row-main">
              <strong>{ticket.subject}</strong>
              <span className="support-ticket-category">{CATEGORY_LABELS[ticket.category]}</span>
            </div>
            <div className="support-ticket-row-meta">
              <span className={`support-badge support-status-${ticket.status}`}>
                {STATUS_LABELS[ticket.status] || ticket.status}
              </span>
              <span className={`support-badge support-priority-${ticket.priority}`}>
                {PRIORITY_LABELS[ticket.priority] || ticket.priority}
              </span>
              <span className="support-ticket-date">
                {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
              </span>
            </div>
          </button>
        ))}
      </div>
    )}
  </div>
);

interface NewTicketFormProps {
  onCancel: () => void;
  onCreated: (ticket: SupportTicket) => void;
}

const NewTicketForm: React.FC<NewTicketFormProps> = ({ onCancel, onCreated }) => {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<SupportTicketCategory>('technical');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [globalError, setGlobalError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    setGlobalError('');
    setFieldErrors({});
    setSubmitting(true);

    try {
      const response = await supportApi.create({ subject, category, message });
      onCreated(response.data);
    } catch (err: any) {
      const apiErrors = err.response?.data?.errors as Record<string, string[]> | undefined;
      if (apiErrors) {
        const mapped: Record<string, string> = {};
        Object.keys(apiErrors).forEach((key) => {
          mapped[key] = apiErrors[key][0];
        });
        setFieldErrors(mapped);
      } else {
        setGlobalError(getApiErrorMessage(err, "Erreur lors de la création du ticket."));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-tab">
      <h2>Nouveau ticket</h2>

      <form onSubmit={handleSubmit} className="settings-form">
        <div className="form-group">
          <label htmlFor="ticket-subject">Sujet</label>
          <input
            id="ticket-subject"
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            maxLength={255}
            disabled={submitting}
            placeholder="Résumez votre demande en quelques mots"
          />
          {fieldErrors.subject && <span className="field-error" role="alert">{fieldErrors.subject}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="ticket-category">Catégorie</label>
          <select
            id="ticket-category"
            value={category}
            onChange={(e) => setCategory(e.target.value as SupportTicketCategory)}
            disabled={submitting}
          >
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          {fieldErrors.category && <span className="field-error" role="alert">{fieldErrors.category}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="ticket-message">Message</label>
          <textarea
            id="ticket-message"
            rows={6}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            maxLength={5000}
            disabled={submitting}
            placeholder="Décrivez votre problème ou votre question"
          />
          {fieldErrors.message && <span className="field-error" role="alert">{fieldErrors.message}</span>}
        </div>

        {globalError && <div className="settings-error" role="alert">{globalError}</div>}

        <div className="support-form-actions">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Envoi...' : 'Créer le ticket'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};

interface SupportTicketDetailViewProps {
  ticketId: number;
  onBack: () => void;
}

const SupportTicketDetailView: React.FC<SupportTicketDetailViewProps> = ({ ticketId, onBack }) => {
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');

  useEffect(() => {
    loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const loadTicket = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await supportApi.get(ticketId);
      setTicket(response.data);
    } catch (err: any) {
      setLoadError(getApiErrorMessage(err, 'Impossible de charger ce ticket.'));
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending || !reply.trim()) return;

    setSendError('');
    setSending(true);
    try {
      await supportApi.reply(ticketId, reply);
      setReply('');
      await loadTicket();
    } catch (err: any) {
      setSendError(getApiErrorMessage(err, "Erreur lors de l'envoi de votre réponse."));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="loading">Chargement...</div>;

  if (loadError || !ticket) {
    return (
      <div className="support-tab">
        <div className="settings-error">{loadError || 'Ticket introuvable.'}</div>
        <button className="btn btn-secondary" onClick={onBack}>
          <NavArrowLeft width={18} height={18} strokeWidth={2} />
          Retour à la liste
        </button>
      </div>
    );
  }

  return (
    <div className="support-tab">
      <button className="btn btn-secondary support-detail-back" onClick={onBack}>
        <NavArrowLeft width={18} height={18} strokeWidth={2} />
        Retour à la liste
      </button>

      <div className="support-ticket-header">
        <h2>{ticket.subject}</h2>
        <div className="support-ticket-row-meta">
          <span className={`support-badge support-status-${ticket.status}`}>
            {STATUS_LABELS[ticket.status] || ticket.status}
          </span>
          <span className={`support-badge support-priority-${ticket.priority}`}>
            {PRIORITY_LABELS[ticket.priority] || ticket.priority}
          </span>
          <span className="support-ticket-category">{CATEGORY_LABELS[ticket.category]}</span>
        </div>
      </div>

      <div className="support-conversation">
        <div className="support-message support-message-user">
          <div className="support-message-body">{ticket.message}</div>
          <div className="support-message-date">{new Date(ticket.created_at).toLocaleString('fr-FR')}</div>
        </div>

        {(ticket.messages || []).map((msg) => (
          <div
            key={msg.id}
            className={`support-message ${msg.is_admin ? 'support-message-admin' : 'support-message-user'}`}
          >
            <div className="support-message-author">{msg.is_admin ? 'Support Ventilio' : 'Vous'}</div>
            <div className="support-message-body">{msg.message}</div>
            <div className="support-message-date">{new Date(msg.created_at).toLocaleString('fr-FR')}</div>
          </div>
        ))}
      </div>

      <form onSubmit={handleReply} className="support-reply-form">
        <textarea
          rows={4}
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          placeholder="Votre réponse..."
          disabled={sending}
          required
        />
        {sendError && <div className="settings-error" role="alert">{sendError}</div>}
        <button type="submit" className="btn btn-primary" disabled={sending || !reply.trim()}>
          <Send width={18} height={18} strokeWidth={2} />
          {sending ? 'Envoi...' : 'Répondre'}
        </button>
      </form>
    </div>
  );
};
